import React, { useState } from "react";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import Loader from "../../components/ui/Loader";
import { useGetProfileQuery } from "../../store/api/authApi";
import { useToast } from "../../contexts/ToastContext";
import { formatDateTime } from "../../utils/formatDate";

const SettingsPage: React.FC = () => {
	const { data: user, isLoading } = useGetProfileQuery();
	const { success, error: showError } = useToast();

	const [emailNotifications, setEmailNotifications] = useState(() => {
		const saved = localStorage.getItem('email_notifications');
		return saved !== null ? saved === 'true' : (user?.email_notifications ?? true);
	});
	const [smsNotifications, setSmsNotifications] = useState(() => {
		const saved = localStorage.getItem('sms_notifications');
		return saved !== null ? saved === 'true' : (user?.sms_notifications ?? false);
	});
	const [isSaving, setIsSaving] = useState(false);

	const handleSave = async () => {
		if (!user) return;

		setIsSaving(true);
		try {
			// For now, we'll save to localStorage until backend supports notification settings
			// In a real implementation, this would call an API endpoint
			localStorage.setItem('email_notifications', String(emailNotifications));
			localStorage.setItem('sms_notifications', String(smsNotifications));
			success("Settings saved successfully!");
		} catch (err: any) {
			showError(
				err?.data?.message || "Failed to save settings. Please try again."
			);
		} finally {
			setIsSaving(false);
		}
	};

	if (isLoading) {
		return <Loader fullScreen size="md" />;
	}

	return (
		<div className="w-full max-w-4xl mx-auto">
						{/* Header */}
						<div className="mb-6">
							<h1 className="text-2xl font-bold text-foreground">
								Account Settings
							</h1>
							<p className="text-muted-foreground mt-1">
								Manage your account preferences and notification settings
							</p>
						</div>

						{/* Notification Settings */}
						<div className="bg-card border border-border rounded-lg p-6 mb-6">
							<h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
								<Icon name="Bell" size={20} />
								Notification Preferences
							</h2>

							<div className="space-y-4">
								{/* Email Notifications */}
								<div className="flex items-center justify-between p-4 border border-border rounded-lg">
									<div className="flex-1">
										<h3 className="font-medium text-foreground">
											Email Notifications
										</h3>
										<p className="text-sm text-muted-foreground mt-1">
											Receive notifications via email
										</p>
									</div>
									<label className="relative inline-flex items-center cursor-pointer">
										<input
											type="checkbox"
											checked={emailNotifications}
											onChange={(e) => setEmailNotifications(e.target.checked)}
											className="sr-only peer"
										/>
										<div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
									</label>
								</div>

								{/* SMS Notifications */}
								<div className="flex items-center justify-between p-4 border border-border rounded-lg">
									<div className="flex-1">
										<h3 className="font-medium text-foreground">
											SMS Notifications
										</h3>
										<p className="text-sm text-muted-foreground mt-1">
											Receive notifications via SMS
										</p>
									</div>
									<label className="relative inline-flex items-center cursor-pointer">
										<input
											type="checkbox"
											checked={smsNotifications}
											onChange={(e) => setSmsNotifications(e.target.checked)}
											className="sr-only peer"
										/>
										<div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
									</label>
								</div>
							</div>

							<div className="mt-6 flex justify-end">
								<Button
									onClick={handleSave}
									disabled={isSaving}
									className="min-w-[120px]"
								>
									{isSaving ? (
										<>
											<Icon name="Loader2" size={16} className="animate-spin mr-2" />
											Saving...
										</>
									) : (
										"Save Changes"
									)}
								</Button>
							</div>
						</div>

						{/* Account Information */}
						<div className="bg-card border border-border rounded-lg p-6">
							<h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
								<Icon name="Info" size={20} />
								Account Information
							</h2>

							<div className="space-y-4">
								<div className="p-4 border border-border rounded-lg">
									<label className="text-sm font-medium text-muted-foreground">
										Email Address
									</label>
									<p className="text-foreground mt-1">{user?.email}</p>
								</div>

								<div className="p-4 border border-border rounded-lg">
									<label className="text-sm font-medium text-muted-foreground">
										Role
									</label>
									<p className="text-foreground mt-1 capitalize">
										{user?.role || "User"}
									</p>
								</div>

								<div className="p-4 border border-border rounded-lg">
									<label className="text-sm font-medium text-muted-foreground">
										Member Since
									</label>
									<p className="text-foreground mt-1">
										{user?.created_at
											? formatDateTime(user.created_at)
											: "N/A"}
									</p>
								</div>
							</div>

							<div className="mt-6">
								<Button
									variant="outline"
									onClick={() => window.location.href = "/profile"}
									className="w-full"
								>
									<Icon name="User" size={16} className="mr-2" />
									Edit Profile Information
								</Button>
							</div>
						</div>
					</div>
	);
};

export default SettingsPage;
